import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, RotateCcw, XCircle, type LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Shared checklist board for The Polished Version platform-admin QA checklists.
 *
 * A self-contained, in-browser checklist a tester (e.g. Ronna) can work through
 * end-to-end. Each scenario has Pass/Fail controls and a notes field. Results
 * are kept in component state, mirrored to localStorage so a long session
 * survives an accidental refresh, and saved to Supabase when the reviewer is
 * online so platform admins can review submissions centrally.
 *
 * Both the UAT and Smoke Test checklists share a single Supabase table
 * (`uat_checklist_results`, one row per reviewer). Each checklist uses its own,
 * non-overlapping scenario ids and loads the whole row before editing, so
 * saving one checklist merges over — and never overwrites — the other
 * checklist's results stored in the same row.
 */

export type Status = 'pass' | 'fail' | null;

export interface ScenarioResult {
  status: Status;
  notes: string;
}

export type Results = Record<string, ScenarioResult>;

export interface Scenario {
  id: string;
  title: string;
  detail?: string;
}

export interface Section {
  id: string;
  title: string;
  description: string;
  icon: LucideIcon;
  scenarios: Scenario[];
}

export interface ChecklistConfig {
  /** Page heading and h2 label. */
  title: string;
  /** Icon shown beside the page heading. */
  icon: LucideIcon;
  /** Short intro paragraph under the heading. */
  intro: string;
  /** localStorage key for the browser fallback copy (unique per checklist). */
  storageKey: string;
  /** Noun used in save-state labels, e.g. "UAT record" / "QA record". */
  recordNoun: string;
  /** Heading for the platform-admin "saved submissions" card. */
  submissionsTitle: string;
  /** Description for the platform-admin "saved submissions" card. */
  submissionsDescription: string;
  /** Empty-state copy for the platform-admin "saved submissions" card. */
  submissionsEmpty: string;
  /** The checklist sections and their scenarios. */
  sections: Section[];
}

type SaveState = 'local-only' | 'loading' | 'saving' | 'saved' | 'error';

interface SavedSubmission {
  id: string;
  user_id: string;
  reviewer_email: string;
  results: Results;
  updated_at: string;
}

function isResults(value: unknown): value is Results {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

function normalizeResults(value: unknown): Results {
  return isResults(value) ? (value as Results) : {};
}

function formatSavedAt(value?: string) {
  if (!value) return 'Not saved yet';
  return new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

function loadLocalResults(storageKey: string): Results {
  try {
    const raw = localStorage.getItem(storageKey);
    return raw ? (JSON.parse(raw) as Results) : {};
  } catch {
    return {};
  }
}

const ChecklistBoard = ({ config }: { config: ChecklistConfig }) => {
  const { user, isPlatformAdmin } = useAuth();
  const allScenarios = useMemo(() => config.sections.flatMap((section) => section.scenarios), [config.sections]);
  const scenarioIds = useMemo(() => new Set(allScenarios.map((scenario) => scenario.id)), [allScenarios]);

  const [results, setResults] = useState<Results>(() => loadLocalResults(config.storageKey));
  const [saveState, setSaveState] = useState<SaveState>('local-only');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [savedSubmissions, setSavedSubmissions] = useState<SavedSubmission[]>([]);
  const [selectedSubmission, setSelectedSubmission] = useState<SavedSubmission | null>(null);

  const isViewingOwnResults = !selectedSubmission || selectedSubmission.user_id === user?.id;
  const visibleResults = selectedSubmission && !isViewingOwnResults ? selectedSubmission.results : results;

  const persistLocal = (next: Results) => {
    try {
      localStorage.setItem(config.storageKey, JSON.stringify(next));
    } catch {
      /* storage unavailable — keep in-memory state only */
    }
  };

  async function loadSavedSubmissions() {
    if (!isPlatformAdmin) return;

    const { data, error } = await supabase
      .from('uat_checklist_results')
      .select('id,user_id,reviewer_email,results,updated_at')
      .order('updated_at', { ascending: false });

    if (error) {
      setSaveError(error.message);
      return;
    }

    setSavedSubmissions(
      (data ?? []).map((submission) => ({
        ...submission,
        results: normalizeResults(submission.results),
      })),
    );
  }

  const saveRemote = async (next: Results) => {
    if (!user?.id || !user.email) {
      setSaveState('local-only');
      return;
    }

    setSaveState('saving');
    setSaveError(null);

    const savedAt = new Date().toISOString();
    const { error } = await supabase
      .from('uat_checklist_results')
      .upsert(
        {
          user_id: user.id,
          reviewer_email: user.email,
          results: next,
          updated_at: savedAt,
        },
        { onConflict: 'user_id' },
      );

    if (error) {
      setSaveState('error');
      setSaveError(error.message);
      return;
    }

    setLastSavedAt(savedAt);
    setSaveState('saved');
    if (isPlatformAdmin) {
      await loadSavedSubmissions();
    }
  };

  const persist = (next: Results) => {
    setSelectedSubmission(null);
    setResults(next);
    persistLocal(next);
    void saveRemote(next);
  };

  async function loadOwnSavedResults() {
    if (!user?.id) {
      setSaveState('local-only');
      return;
    }

    setSaveState('loading');
    setSaveError(null);

    const { data, error } = await supabase
      .from('uat_checklist_results')
      .select('id,user_id,reviewer_email,results,updated_at')
      .eq('user_id', user.id)
      .maybeSingle();

    if (error) {
      setSaveState('error');
      setSaveError(error.message);
      return;
    }

    if (data) {
      // The row is shared across checklists — keep every key so saving this
      // checklist never drops scenarios owned by the other checklist.
      const remoteResults = normalizeResults(data.results);
      setResults(remoteResults);
      persistLocal(remoteResults);
      setLastSavedAt(data.updated_at);
      setSaveState('saved');
      return;
    }

    setSaveState('local-only');
  }

  useEffect(() => {
    void loadOwnSavedResults();
    void loadSavedSubmissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isPlatformAdmin, config.storageKey]);

  const update = (id: string, partial: Partial<ScenarioResult>) => {
    if (!isViewingOwnResults) return;
    const current = results[id] ?? { status: null, notes: '' };
    persist({ ...results, [id]: { ...current, ...partial } });
  };

  const setStatus = (id: string, status: Status) => {
    if (!isViewingOwnResults) return;
    const current = results[id]?.status ?? null;
    update(id, { status: current === status ? null : status });
  };

  const resetAll = () => {
    if (!isViewingOwnResults) {
      setSelectedSubmission(null);
      return;
    }
    // Only clear this checklist's scenarios; preserve any sibling-checklist keys.
    const next: Results = { ...results };
    for (const id of scenarioIds) delete next[id];
    persist(next);
  };

  const totals = useMemo(() => {
    let passed = 0;
    let failed = 0;
    for (const scenario of allScenarios) {
      const status = visibleResults[scenario.id]?.status;
      if (status === 'pass') passed += 1;
      else if (status === 'fail') failed += 1;
    }
    const total = allScenarios.length;
    return { total, passed, failed, pending: total - passed - failed };
  }, [visibleResults, allScenarios]);

  const saveStateLabel = {
    'local-only': 'Saved in this browser only',
    loading: 'Loading saved results…',
    saving: `Saving to shared ${config.recordNoun}…`,
    saved: `Saved to shared ${config.recordNoun}: ${formatSavedAt(lastSavedAt ?? undefined)}`,
    error: 'Shared save failed — browser copy retained',
  }[saveState];

  const HeadingIcon = config.icon;

  return (
    <div className="space-y-6 p-4 md:p-8">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h2 className="flex items-center gap-2 font-display text-3xl text-foreground">
            <HeadingIcon className="h-7 w-7 text-primary" />
            {config.title}
          </h2>
          <p className="mt-1 max-w-2xl text-muted-foreground">{config.intro}</p>
          {!isViewingOwnResults && selectedSubmission && (
            <p className="mt-2 text-sm font-medium text-primary">
              Viewing {selectedSubmission.reviewer_email}&apos;s saved submission from {formatSavedAt(selectedSubmission.updated_at)}.
              Editing is disabled while viewing another reviewer.
            </p>
          )}
        </div>
        <Button variant="outline" onClick={resetAll} className="shrink-0 gap-2">
          <RotateCcw className="h-4 w-4" />
          {isViewingOwnResults ? 'Reset all' : 'Back to my checklist'}
        </Button>
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-center gap-3 p-4">
          <Badge variant="outline" className="gap-1 text-sm">
            Total: {totals.total}
          </Badge>
          <Badge variant="outline" className="gap-1 border-green-500/30 bg-green-500/10 text-sm text-green-600 dark:text-green-400">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Passed: {totals.passed}
          </Badge>
          <Badge variant="outline" className="gap-1 border-red-500/30 bg-red-500/10 text-sm text-red-600 dark:text-red-400">
            <XCircle className="h-3.5 w-3.5" />
            Failed: {totals.failed}
          </Badge>
          <Badge variant="outline" className="gap-1 text-sm text-muted-foreground">
            Not tested: {totals.pending}
          </Badge>
          <Badge variant="outline" className="gap-1 text-sm text-muted-foreground">
            {saveStateLabel}
          </Badge>
          {saveError && <span className="text-sm text-destructive">{saveError}</span>}
        </CardContent>
      </Card>

      {isPlatformAdmin && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{config.submissionsTitle}</CardTitle>
            <CardDescription>{config.submissionsDescription}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {savedSubmissions.length === 0 ? (
              <p className="text-sm text-muted-foreground">{config.submissionsEmpty}</p>
            ) : (
              savedSubmissions.map((submission) => (
                <div
                  key={submission.id}
                  className="flex flex-col gap-3 rounded-lg border p-3 md:flex-row md:items-center md:justify-between"
                >
                  <div>
                    <p className="font-medium">{submission.reviewer_email}</p>
                    <p className="text-sm text-muted-foreground">Last saved: {formatSavedAt(submission.updated_at)}</p>
                  </div>
                  <Button type="button" variant="outline" onClick={() => setSelectedSubmission(submission)}>
                    View saved result
                  </Button>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      )}

      <div className="space-y-6">
        {config.sections.map((section) => {
          const Icon = section.icon;
          const sectionPassed = section.scenarios.filter((s) => visibleResults[s.id]?.status === 'pass').length;
          return (
            <Card key={section.id}>
              <CardHeader>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Icon className="h-5 w-5 text-primary" />
                      {section.title}
                    </CardTitle>
                    <CardDescription className="mt-1">{section.description}</CardDescription>
                  </div>
                  <Badge variant="outline" className="shrink-0">
                    {sectionPassed}/{section.scenarios.length}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {section.scenarios.map((scenario, index) => {
                  const result = visibleResults[scenario.id] ?? { status: null, notes: '' };
                  return (
                    <div key={scenario.id}>
                      {index > 0 && <Separator className="mb-4" />}
                      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                        <div className="min-w-0">
                          <p className="font-medium text-foreground">{scenario.title}</p>
                          {scenario.detail && (
                            <p className="mt-0.5 text-sm text-muted-foreground">{scenario.detail}</p>
                          )}
                        </div>
                        <div className="flex shrink-0 gap-2" role="group" aria-label={`${scenario.title} result`}>
                          <Button
                            type="button"
                            size="sm"
                            variant={result.status === 'pass' ? 'default' : 'outline'}
                            aria-pressed={result.status === 'pass'}
                            disabled={!isViewingOwnResults}
                            onClick={() => setStatus(scenario.id, 'pass')}
                            className={cn(
                              'gap-1',
                              result.status === 'pass' && 'bg-green-600 text-white hover:bg-green-700',
                            )}
                          >
                            <CheckCircle2 className="h-4 w-4" />
                            Pass
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant={result.status === 'fail' ? 'default' : 'outline'}
                            aria-pressed={result.status === 'fail'}
                            disabled={!isViewingOwnResults}
                            onClick={() => setStatus(scenario.id, 'fail')}
                            className={cn(
                              'gap-1',
                              result.status === 'fail' && 'bg-red-600 text-white hover:bg-red-700',
                            )}
                          >
                            <XCircle className="h-4 w-4" />
                            Fail
                          </Button>
                        </div>
                      </div>
                      <Textarea
                        aria-label={`Notes for ${scenario.title}`}
                        placeholder="Notes"
                        value={result.notes}
                        disabled={!isViewingOwnResults}
                        onChange={(event) => update(scenario.id, { notes: event.target.value })}
                        className="mt-3 min-h-[60px]"
                      />
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default ChecklistBoard;
